// Pure module: pair-completion evolution definitions + stat-delta math.
// No Phaser deps; safe to import from runStore or pure tests.

import type { PerkId, Stats } from './perks';

export type EvolutionId =
  | 'plasma_storm'
  | 'pinball_mode'
  | 'tesla_coil'
  | 'phoenix_strike'
  | 'heavy_orbit'
  | 'toxic_pull';

export type EvolutionDef = {
  id: EvolutionId;
  pair: readonly [PerkId, PerkId];
};

/**
 * The six Phase 2t pair → evolution mappings. Pairs are unordered;
 * `computeEvolutions` checks set membership.
 */
export const EVOLUTION_DEFS: readonly EvolutionDef[] = [
  { id: 'plasma_storm',   pair: ['multishot_forward', 'skewer'] },
  { id: 'pinball_mode',   pair: ['bouncy_walls', 'ricochet'] },
  { id: 'tesla_coil',     pair: ['chain_lightning', 'cryo'] },
  { id: 'phoenix_strike', pair: ['ignite', 'explosive_kills'] },
  { id: 'heavy_orbit',    pair: ['orbit_blade', 'big_bullet'] },
  { id: 'toxic_pull',     pair: ['toxin', 'magnetic'] },
];

/** Returns the evolution ids whose pair is fully owned. Order: EVOLUTION_DEFS order. */
export function computeEvolutions(owned: ReadonlySet<PerkId>): EvolutionId[] {
  const out: EvolutionId[] = [];
  for (const e of EVOLUTION_DEFS) {
    if (owned.has(e.pair[0]) && owned.has(e.pair[1])) out.push(e.id);
  }
  return out;
}

/** Applies one evolution's stat delta (returns a new Stats; input unchanged). */
export function applyEvolution(stats: Stats, id: EvolutionId): Stats {
  switch (id) {
    case 'plasma_storm':
      return { ...stats, pierce: (stats.pierce ?? 0) + 2 };
    case 'pinball_mode':
      return {
        ...stats,
        bouncyWalls: (stats.bouncyWalls ?? 0) + 1,
        ricochet: (stats.ricochet ?? 0) + 1,
      };
    case 'tesla_coil':
      return { ...stats, chainAlsoFreezes: true };
    case 'phoenix_strike':
      return { ...stats, burnKillsExplode: true };
    case 'heavy_orbit':
      return { ...stats, bladeDamageMult: (stats.bladeDamageMult ?? 1) * 1.5 };
    case 'toxic_pull':
      return { ...stats, lootPickupRadius: (stats.lootPickupRadius ?? 60) * 1.5 };
  }
}
